import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CreateLiveDto,
  Live,
  LiveSearchResult,
  UserInformation,
  ChangeRoleDto,
  ChangeRightsDto,
  ExistingConf,
  LiveCodeValidationResponse,
} from '@shared/models/live';
import { EncodingFormat } from '@shared/models/equipment';
import { InvitePeopleDto, LiveConflictCheckDto } from '@shared/models/calendar';

@Service()
export class LiveApi {
  private readonly httpClient = inject(HttpClient);

  public getLive(code: string): Observable<Live> {
    return this.httpClient.get<Live>(`${environment.apiUrl}/Live/GetLiveByCode?code=${code}`);
  }

  public getAvailableEncodingFormats(): Observable<EncodingFormat[]> {
    return this.httpClient.get<EncodingFormat[]>(`${environment.apiUrl}/LiveCase/GetAllEncodingFormats`);
  }

  public searchLive(name: string): Observable<LiveSearchResult[]> {
    const params = new HttpParams().set('searchString', name);
    return this.httpClient.get<LiveSearchResult[]>(`${environment.apiUrl}/Live/SearchLive`, { params: params });
  }

  public createLive(live: CreateLiveDto): Observable<Live> {
    return this.httpClient.post<Live>(`${environment.apiUrl}/Live/CreateLive`, live);
  }

  public isAllowedToJoinLive(liveCode: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`${environment.apiUrl}/Live/IsAllowedToJoinLive?liveCode=${liveCode}`);
  }

  public startConference(code: string): Observable<Live> {
    return this.httpClient.patch<Live>(`${environment.apiUrl}/Live/StartLive?liveCode=${code}`, null);
  }

  public hasScheduledLiveConflict(): Observable<LiveConflictCheckDto> {
    return this.httpClient.get<LiveConflictCheckDto>(`${environment.apiUrl}/Live/HasScheduledLiveConflict`);
  }

  public endLive(reference: string): Observable<boolean> {
    return this.httpClient.delete<boolean>(`${environment.apiUrl}/Live/EndLive?liveCode=${reference}`);
  }

  public editUserInfo(code: string | undefined, externalId: string, connectionId: string): Observable<UserInformation | null> {
    const userInfo = {
      code: code,
      externalId: externalId,
      connectionId: connectionId,
    };
    return this.httpClient.patch<UserInformation | null>(`${environment.apiUrl}/Live/EditUserInfo`, userInfo);
  }

  /** Equipment-only endpoint; the backend resolves the caller from its JWT. */
  public setPublisherTransport(liveCode: string | undefined, protocol?: string, relayProtocol?: string): Observable<void> {
    const dto = {
      liveCode: liveCode,
      protocol: protocol ?? null,
      relayProtocol: relayProtocol ?? null,
    };
    return this.httpClient.patch<void>(`${environment.apiUrl}/Live/SetPublisherTransport`, dto);
  }

  public sendEmailInvitation(inviteToLive: InvitePeopleDto): Observable<unknown> {
    return this.httpClient.post(`${environment.apiUrl}/Live/SendEmailInvitation`, inviteToLive);
  }

  public notifyLiveStarts(liveId: number): Observable<void> {
    return this.httpClient.get<void>(`${environment.apiUrl}/live/NotifyLiveStarts`, { params: { liveId: liveId } });
  }

  public changeUsersRole(changerole: ChangeRoleDto): Observable<boolean> {
    return this.httpClient.post<boolean>(`${environment.apiUrl}/Live/ChangeUserRole`, changerole);
  }

  public giveAppRights(changeRights: ChangeRightsDto): Observable<unknown> {
    return this.httpClient.put(`${environment.apiUrl}/Live/GiveLiveRights`, changeRights);
  }

  public revokeAppRights(changeRights: ChangeRightsDto): Observable<unknown> {
    return this.httpClient.patch(`${environment.apiUrl}/Live/RevokeLiveRights`, changeRights);
  }

  public generateUniqueLiveCode(): Observable<string> {
    return this.httpClient.get(`${environment.apiUrl}/Live/GenerateUniqueLiveCode`, { responseType: 'text' });
  }

  public validateLiveCodeAsync(liveCode: string): Observable<LiveCodeValidationResponse> {
    return this.httpClient.get<LiveCodeValidationResponse>(`${environment.apiUrl}/Live/ValidateLiveCode`, { params: { code: liveCode } });
  }

  public liveExistForEquipment(equipmentId: string | null): Observable<ExistingConf> {
    return this.httpClient.get<ExistingConf>(`${environment.apiUrl}/Live/LiveExistForEquipment?equipmentId=${equipmentId}`);
  }
}
